import { AlertCircle, CheckCircle2, Copy, Inbox, X } from 'lucide-react'
import { useState } from 'react'

export const PageHeader = ({ title, subtitle, actions }) => (
	<div className="mb-6 flex flex-wrap items-start justify-between gap-4">
		<div>
			<h1 className="text-2xl font-bold text-gray-900">{title}</h1>
			{subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
		</div>
		{actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
	</div>
)

export const Card = ({ title, description, actions, children, className = '' }) => (
	<section className={`bg-white shadow rounded-lg ${className}`}>
		{(title || actions) && (
			<div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
				<div>
					{title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
					{description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
				</div>
				{actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
			</div>
		)}
		<div className="px-5 py-4">{children}</div>
	</section>
)

const variants = {
	primary:
		'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 border-transparent',
	secondary:
		'bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500 border-gray-300',
	danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 border-transparent',
}

export const Button = ({
	variant = 'primary',
	type = 'button',
	className = '',
	children,
	...props
}) => (
	<button
		type={type}
		className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${className}`}
		{...props}
	>
		{children}
	</button>
)

const fieldClass =
	'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50'

export const TextInput = ({ label, id, className = '', ...props }) => (
	<div className={className}>
		{label && (
			<label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
				{label}
			</label>
		)}
		<input id={id} className={fieldClass} {...props} />
	</div>
)

export const Select = ({ label, id, options = [], className = '', ...props }) => (
	<div className={className}>
		{label && (
			<label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
				{label}
			</label>
		)}
		<select id={id} className={fieldClass} {...props}>
			{options.map((option) => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</select>
	</div>
)

// Painel lateral (desliza da direita), para acoes que precisam de um pequeno
// formulario sem sair da tela atual - ex.: sincronizar saldo com um intervalo
// de datas. Fecha ao clicar fora ou no X.
export const Drawer = ({ open, title, description, onClose, children }) => {
	if (!open) return null

	return (
		<div className="fixed inset-0 z-50 flex justify-end">
			<div className="absolute inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
			<div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
				<div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
					<div>
						{title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
						{description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
					</div>
					<button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
						<X className="h-5 w-5" />
					</button>
				</div>
				<div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
			</div>
		</div>
	)
}

export const Alert = ({ type = 'error', children, onDismiss }) => {
	if (!children) return null
	const isError = type === 'error'
	const Icon = isError ? AlertCircle : CheckCircle2

	return (
		<div
			className={`mb-4 flex items-start gap-3 rounded-md p-4 text-sm ${
				isError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
			}`}
		>
			<Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
			<div className="flex-1 break-words">{children}</div>
			{onDismiss && (
				<button type="button" onClick={onDismiss} className="text-xs underline">
					fechar
				</button>
			)}
		</div>
	)
}

const badgeTones = {
	green: 'bg-green-100 text-green-800',
	red: 'bg-red-100 text-red-800',
	yellow: 'bg-yellow-100 text-yellow-800',
	blue: 'bg-blue-100 text-blue-800',
	gray: 'bg-gray-100 text-gray-800',
}

export const Badge = ({ tone = 'gray', children }) => (
	<span
		className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeTones[tone] || badgeTones.gray}`}
	>
		{children}
	</span>
)

// Mapeia os enums da API para uma cor de badge
export const statusTone = (status) => {
	switch (status) {
		case 'ACTIVE':
		case 'APPROVED':
			return 'green'
		case 'REPROVED':
		case 'CLOSED':
		case 'CANCELED':
		case 'LOCKED':
		case 'REJECTED':
			return 'red'
		case 'PENDING':
			return 'yellow'
		default:
			return typeof status === 'string' && status.startsWith('PENDING') ? 'yellow' : 'gray'
	}
}

export const DetailItem = ({ label, children }) => (
	<div>
		<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
		<dd className="mt-1 break-words text-sm text-gray-900">
			{children === null || children === undefined || children === '' ? '—' : children}
		</dd>
	</div>
)

// classes completas para o Tailwind conseguir detectar no build
const gridCols = {
	2: 'lg:grid-cols-2',
	3: 'lg:grid-cols-3',
	4: 'lg:grid-cols-4',
}

export const DetailGrid = ({ children, cols = 3 }) => (
	<dl className={`grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 ${gridCols[cols] || gridCols[3]}`}>
		{children}
	</dl>
)

export const EmptyState = ({ message = 'Nenhum registro encontrado' }) => (
	<div className="flex flex-col items-center justify-center py-12 text-gray-400">
		<Inbox className="mb-2 h-8 w-8" />
		<p className="text-sm">{message}</p>
	</div>
)

export const CopyableId = ({ value }) => {
	const [copied, setCopied] = useState(false)

	if (!value) return <span>—</span>

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		} catch {
			// clipboard indisponivel (http sem permissao) — ignora
		}
	}

	return (
		<span className="inline-flex items-center gap-1.5">
			<code className="font-mono text-xs text-gray-700">{value}</code>
			<button
				type="button"
				onClick={handleCopy}
				title="Copiar"
				className="text-gray-400 hover:text-gray-600"
			>
				{copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
			</button>
		</span>
	)
}

// Copia um valor longo (ex.: link de biometria) sem exibi-lo por inteiro.
export const CopyButton = ({ value, label = 'Copiar', copiedLabel = 'Copiado' }) => {
	const [copied, setCopied] = useState(false)

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		} catch {
			// clipboard indisponivel (http sem permissao) — ignora
		}
	}

	return (
		<button
			type="button"
			onClick={handleCopy}
			title={value}
			className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
		>
			{copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
			{copied ? copiedLabel : label}
		</button>
	)
}

export const JsonViewer = ({ data }) => (
	<pre className="max-h-96 overflow-auto rounded-md bg-gray-900 p-4 text-xs leading-relaxed text-gray-100">
		{JSON.stringify(data, null, 2)}
	</pre>
)

export const formatDateTime = (value) => {
	if (!value) return '—'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return value
	return date.toLocaleString('pt-BR')
}

export const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '—'
	const number = Number(value)
	if (Number.isNaN(number)) return value
	return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export const formatPercentage = (value) => {
	if (value === null || value === undefined || value === '') return '—'
	const number = Number(value)
	if (Number.isNaN(number)) return value
	return `${number.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
}

export const formatDocument = (value) => {
	if (!value) return '—'
	const digits = String(value).replace(/\D/g, '')
	if (digits.length === 11) {
		return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
	}
	if (digits.length === 14) {
		return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
	}
	return value
}
